import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { SignUpDto } from "#modules/auth/infra/signUp.api";
import { useSignUp } from "#modules/auth/infra/useSignUp";
import { ROUTES } from "#shared/routes";
import { Center } from "#shared/view/components/Center";
import { Flex } from "#shared/view/components/Flex";
import { Image } from "#shared/view/components/Image";
import { Paper } from "#shared/view/components/Paper";
import { PasswordInput } from "#shared/view/components/PasswordInput";
import { Stack } from "#shared/view/components/Stack";
import { ThreeStepsStepper } from "#shared/view/components/Stepper";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { TextInput } from "#shared/view/designSystem/inputs/TextInput";

export const SignUpPage = () => {
  const navigate = useNavigate();
  const { signUp, isPending } = useSignUp({
    onSuccess: () => {
      navigate(ROUTES.partnerSetUser);
    },
  });

  const [passwordDefault, setPasswordDefault] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpDto>();

  const { t } = useTranslation();

  const onSubmit = async (data: SignUpDto) => {
    if (data.password !== data.confirmPassword) {
      setPasswordDefault(true);

      return;
    }
    signUp(data);
  };

  return (
    <Paper style={styles.paper}>
      <Paper p="lg" shadow="lg">
        <Stack gap={"xs"} align="center">
          <Flex align="center" justify="center">
            <Image src="/logo-fabera-login.png" w="139" h="60" />
          </Flex>
          <Flex align="center" justify="center">
            <ThreeStepsStepper
              active={0}
              firstStep={t("login.step1")}
              firstDescription={t("login.step1Description")}
              secondStep={t("login.step2")}
              secondDescription={t("login.step2Description")}
              finalStep={t("login.step3")}
              finalDescription={t("login.step3Description")}
            />
          </Flex>
          <form onSubmit={handleSubmit(onSubmit)} style={{ minWidth: 400 }}>
            <TextInput
              mt="xs"
              withAsterisk
              label={t("login.emailAddress")}
              error={
                errors.email
                  ? errors.email.type === "required"
                    ? t("login.emailRequired")
                    : t("login.emailInvalid")
                  : null
              }
              {...register("email", {
                required: true,
                pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
              })}
            />
            <PasswordInput
              mt="xs"
              label={t("login.password")}
              {...register("password", {
                required: true,
                minLength: 8,
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+}{":;'?><\\/.,-]).{8,}$/,
              })}
            />
            {errors.password ? (
              errors.password.type === "required" ? (
                <Text size="xs" c="red" inline>
                  {t("login.passwordRequired")}
                </Text>
              ) : errors.password.type === "minLength" ? (
                <Text size="xs" c="red" inline>
                  {t("login.passwordMinLength")}
                </Text>
              ) : (
                <Text size="xs" c="red" inline>
                  {t("login.passwordConstraints")}
                </Text>
              )
            ) : null}
            <PasswordInput
              mt="xs"
              label={t("login.confirmPassword")}
              {...register("confirmPassword", {
                required: true,
              })}
            />
            {errors.confirmPassword ? (
              <Text size="xs" c="red" inline>
                {t("login.confirmPasswordRequired")}
              </Text>
            ) : passwordDefault ? (
              <Text size="xs" c="red" inline>
                {t("login.confirmPasswordInvalid")}
              </Text>
            ) : null}
            <Center>
              <Button mt="xs" size="md" loading={isPending} variant="filled" type="submit">
                {t("login.signUp")}
              </Button>
            </Center>
          </form>
        </Stack>
      </Paper>
    </Paper>
  );
};

const styles = {
  paper: {
    height: "100vh",
    width: "100wh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--Blue-gradient, linear-gradient(103deg, #2955E2 1.95%, #0B267B 91.53%))",
  },
};
